/**
 * Utility functions for word operations in the Uyghur Word Chain Game
 */

/**
 * Get the last letter of a word
 * @param {string} word - The word to get the last letter from
 * @returns {string} The last letter of the word or empty string if word is empty
 */
export const getLastLetter = (word) => {
    if (!word || word.length === 0) return '';
    return word[word.length - 1];
  };
  
  /**
   * Get the first letter of a word
   * @param {string} word - The word to get the first letter from
   * @returns {string} The first letter of the word or empty string if word is empty
   */
  export const getFirstLetter = (word) => {
    if (!word || word.length === 0) return '';
    return word[0];
  };
  
  /**
   * Get the effective letter to match for continuing the word chain
   * This handles the special case of Uyghur vowels (ا، ە، و، ۆ، ۇ، ۈ، ى، ې) 
   * that need to be preceded by ئ (hamza)
   * @param {string} letter - The original last letter
   * @returns {string} The effective letter for matching (either the original letter or ئ for certain vowels)
   */
  export const getEffectiveLetterForMatching = (letter) => {
    // List of Uyghur vowels that require the hamza (ئ) at the beginning of words
    const vowelsRequiringHamza = ['ا', 'ە', 'و', 'ۆ', 'ۇ', 'ۈ', 'ى', 'ې'];
    
    if (vowelsRequiringHamza.includes(letter)) {
      return 'ئ'; // Return hamza for these vowels
    }
    
    return letter;
  };
  
  /**
   * Check if the first letter of a word matches the specified letter,
   * accounting for Uyghur orthographic rules
   * @param {string} word - The word to check
   * @param {string} letter - The letter to match against
   * @returns {boolean} True if the word can follow the letter according to Uyghur rules
   */
  export const canWordFollowLetter = (word, letter) => {
    if (!word || word.length === 0) return false;
    
    // Get the effective letter for matching (either the original or ئ for vowels)
    const effectiveLetter = getEffectiveLetterForMatching(letter);
    
    // If the effective letter is hamza (ئ), we need special handling
    if (effectiveLetter === 'ئ') {
      // For hamza, either the word starts with hamza directly
      if (word[0] === 'ئ') {
        // If the word starts with hamza, check if the second letter matches the original letter
        // Only for words that are long enough to have a second letter
        if (word.length > 1 && letter !== 'ئ') {
          return word[1] === letter;
        }
        return true;
      }
      return false;
    }
    
    // For non-vowel letters, regular matching applies
    return word[0] === effectiveLetter;
  };
  
  /**
   * Check if a word is a valid Uyghur word
   * @param {string} word - The word to validate
   * @param {Array} dictionary - The dictionary to validate against
   * @returns {boolean} True if the word is valid, false otherwise
   */
  export const isValidUyghurWord = (word, dictionary) => {
    return dictionary.includes(word);
  };
  
  /**
   * Check if a word can follow another word in the chain
   * @param {string} previousWord - The previous word in the chain
   * @param {string} nextWord - The candidate next word
   * @param {boolean} isNgRule - Whether the special rule for 'ڭ' is active
   * @returns {boolean} True if the word can follow, false otherwise
   */
  export const canWordFollow = (previousWord, nextWord, isNgRule = false) => {
    if (!previousWord || !nextWord) return false;
    
    const lastLetter = getLastLetter(previousWord);
    
    // Special rule for ڭ
    if (lastLetter === 'ڭ' && isNgRule) {
      return true; // Any word can follow a word ending with ڭ when the rule is active
    }
    
    return canWordFollowLetter(nextWord, lastLetter);
  };
  
  /**
   * Generate a hint for a letter
   * @param {string} letter - The letter to generate a hint for
   * @returns {string} A hint with example words
   */
  export const generateHintForLetter = (letter) => {
    // Special handling for vowels that require hamza
    const vowelsRequiringHamza = ['ا', 'ە', 'و', 'ۆ', 'ۇ', 'ۈ', 'ى', 'ې'];
    
    if (vowelsRequiringHamza.includes(letter)) {
      const hintMap = {
        'ا': 'ئالما، ئانا، ئاتا',
        'ە': 'ئەينەك، ئەدەب، ئەقىل',
        'و': 'ئوت، ئورۇن، ئويۇن',
        'ۆ': 'ئۆي، ئۆردەك، ئۆت',
        'ۇ': 'ئۇيغۇر، ئۇخلا، ئۇزۇن',
        'ۈ': 'ئۈزۈم، ئۈستەل، ئۈمىد',
        'ى': 'ئىلىم، ئىگە، ئىنكاس',
        'ې': 'ئېتىز، ئېغىز، ئېشەك'
      };
      
      return hintMap[letter] || '';
    }
    
    // Regular hint map for other letters
    const hintMap = {
      'ئ': 'ئالما، ئاتا، ئانا',
      'ب': 'باغ، بالا، باھار',
      'ت': 'تاغ، تاش، تۇخۇم',
      'ج': 'جان، جاھان، جۈمە',
      'چ': 'چاي، چاشقان، چىراغ',
      'خ': 'خەت، خورما، خالتا',
      'د': 'دەريا، دالا، دوست',
      'ر': 'راۋاپ، رەسىم، رايون',
      'ز': 'زامان، زەيتۇن، زىيارەت',
      'س': 'سۇ، سايا، سورۇن',
      'ش': 'شامال، شاپتۇل، شېكەر',
      'غ': 'غازا، غەلىبە، غايە',
      'ق': 'قار، قۇش، قوي',
      'ك': 'كىتاب، كۈن، كۆل',
      'گ': 'گۈل، گۆش، گىلەم',
      'ل': 'لالا، لايىق، لىمون',
      'م': 'مۇز، مېۋە، مەكتەپ',
      'ن': 'نان، ناخشا، نەي',
      'ھ': 'ھاۋا، ھايات، ھېيت',
      'پ': 'پاختا، پۇل، پەرواز',
      'ژ': 'ژۇرنال، ژىلىتكا',
      'ۋ': 'ۋاقىت، ۋەتەن، ۋەدە',
      'ف': 'فىلىم، فابرىكا، فىزىكا',
      'ي': 'يول، يۇلتۇز، يامغۇر',
    };
    
    return hintMap[letter] || '';
  };
  
  /**
   * Get difficulty level description
   * @param {string} difficulty - The difficulty level ('easy', 'medium', 'hard')
   * @returns {string} A description of the difficulty level
   */
  export const getDifficultyDescription = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'ئاسان: روبوت قىسقا سۆزلەرنى تاللايدۇ، داۋاملاشتۇرۇش ئاسان بولىدۇ.';
      case 'medium':
        return 'ئوتتۇرا: تەڭپۇڭلاشتۇرۇلغان قىيىنلىق، خىلمۇخىل سۆزلەر.';
      case 'hard':
        return 'قىيىن: روبوت قىيىن ھەرپلەر بىلەن ئاخىرلىشىدىغان سۆزلەرنى تاللاشقا تىرىشىدۇ.';
      default:
        return '';
    }
  };
  
  /**
   * Get a list of difficult letters that make the game harder
   * @returns {Array} Array of difficult letters
   */
  export const getDifficultLetters = () => {
    return ['ڭ', 'ژ', 'ف', 'ۋ', 'چ', 'خ'];
  };
  
  /**
   * Process a word dictionary from text format
   * @param {string} text - The raw text from the dictionary file
   * @returns {Object} Processed dictionary with all words and words indexed by first letter
   */
  export const processDictionaryText = (text) => {
    // Split text into words, trim whitespace, and filter out empty entries
    const words = text.split('\n').map(word => word.trim()).filter(word => word.length > 0);
    
    // Create an index of words by first letter for quick access
    const wordsByFirstLetter = {};
    
    // Create an index of words by effective letter (handling the hamza+vowel special case)
    const wordsByEffectiveLetter = {};
    
    words.forEach(word => {
      if (word.length > 0) {
        // Store by first letter
        const firstLetter = word[0];
        if (!wordsByFirstLetter[firstLetter]) {
          wordsByFirstLetter[firstLetter] = [];
        }
        wordsByFirstLetter[firstLetter].push(word);
        
        // Special handling for words starting with hamza + vowel
        if (word[0] === 'ئ' && word.length > 1) {
          const secondLetter = word[1];
          if (!wordsByEffectiveLetter[secondLetter]) {
            wordsByEffectiveLetter[secondLetter] = [];
          }
          wordsByEffectiveLetter[secondLetter].push(word);
        }
      }
    });
    
    return {
      all: words,
      byFirstLetter: wordsByFirstLetter,
      byEffectiveLetter: wordsByEffectiveLetter
    };
  };
  
  /**
   * Get words that can follow after a given letter
   * @param {Object} dictionary - The processed dictionary
   * @param {string} letter - The letter that words should follow after
   * @returns {Array} Array of words that can follow after the given letter
   */
  export const getWordsThatCanFollow = (dictionary, letter) => {
    // List of Uyghur vowels that require the hamza (ئ) at the beginning of words
    const vowelsRequiringHamza = ['ا', 'ە', 'و', 'ۆ', 'ۇ', 'ۈ', 'ى', 'ې'];
    
    if (vowelsRequiringHamza.includes(letter)) {
      // For vowels, look up words that start with hamza followed by the vowel
      return dictionary.byEffectiveLetter[letter] || [];
    }
    
    // For other letters, standard lookup
    return dictionary.byFirstLetter[letter] || [];
  };